<template>
  <main class="patient-add">
    <form
      v-if="isDefaultMedcardNumberFetched"
      class="patient-add__body"
      v-on:submit.prevent="onSubmitCreatePatient">
      <div class="patient-add__header">
        <h1 class="patient-add__title-print">Пациент</h1>
        <h1 class="patient-add__title noprint">Новый пациент</h1>
        <div
          v-if="!isMobile"
          class="patient-add__buttons">
          <button
            type="submit"
            class="patient-add__button-save"
            v-if="hasSavePatient">
            Сохранить
          </button>
        </div>
      </div>
      <form-create-patient
        v-model="patient"
        v-on:focus="onCheckPatientInfo"
        v-on:blur="onCheckPatientInfo"
        v-bind:violationCauses="violationCauses" />
      <fieldset class="patient-add__sex">
        <legend>Пол</legend>
        <label class="patient-add__sex__radio">
          <input name="sex" type="radio" v-bind:value="$options.sexMale" v-model="sexId" />
          <span>Мужской</span>
        </label>
        <label class="patient-add__sex__radio">
          <input name="sex" type="radio" v-bind:value="$options.sexFemale" v-model="sexId" />
          <span>Женский</span>
        </label>
        <label class="patient-add__sex__radio">
          <input name="sex" type="radio" v-bind:value="$options.sexNone" v-model="sexId" />
          <span>Не указан</span>
        </label>
      </fieldset>
      <section
        v-if="hasViewContact"
        class="patient-add__contact"
        name="contact">
        <h3 class="patient-add__caption">Контакты</h3>
        <form-patient-contacts
          v-bind:violation-causes="violationCauses"
          v-model="contacts"
          v-on:input="onInputContacts"
        />
      </section>
      <section>
        <template v-if="matchedPatients.length">
          <div class="patient-add__double">
            <div>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <span class="patient-add__double__title">
                      Внимание! Есть мед.карты с такими данными.
                    </span>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <button
                      type="button"
                      v-for="patient in matchedPatients"
                      v-bind:key="`matched-by-name-and-phone${patient.value}`"
                      v-on:click="onSelectMatchedPatient(patient.value)"
                      class="patient-add__double__error"
                    >{{ patient.title }}</button>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </div>
        </template>
      </section>
      <section
        class="patient-add__documents"
        name="document">
        <h3 class="patient-add__caption">Документы</h3>
        <input-snils
          class="patient-add__documents-snils"
          optional
          v-bind:violations="snilsInputError"
          v-model="documents.snils"
        />
        <input-inn
          class="patient-add__documents-snils"
          v-model="identification.inn"
          optional
          v-bind:violations="innInputErrors" />
      </section>
      <section
        class="patient-add__ticket"
        name="passport">
        <div class="patient-add__identification">
          <form-identification
            v-on:on-model-update="onUpdateIdentification"
            :input-errors=violationCauses
            :init-identification=identification
            :is-show-inn="false"
          />
        </div>
        <button
          type="submit"
          class="patient-add__button-save"
          v-if="hasSavePatient">
          Сохранить
        </button>
      </section>
    </form>
  </main>
</template>
<script>
import FormCreatePatient from '@/components/FormCreatePatient.vue';
import FormPatientContacts, { PatientContacts } from '@/components/FormPatientContacts.vue';
import dateMixin from '@/mixins/dateMixin';
import focusMixin from '@/mixins/focusMixin';
import notifierMixin from '@/mixins/notifierMixin';
import { Documents, GetMatchedPatientsRequest, Identification } from '@/storage/gateways/PatientGateway';
import mobileMixin from '@/mixins/mobileMixin';
import FormIdentification from '@/components/FormIdentification.vue';
import InputSnils from '@/components/InputSnils.vue';
import InputInn from '@/components/InputInn.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.violationCauses ? vm.violationCauses[cause] : []
);

/**
 * @description Страница создания/редактирования пациента.
 * @type {any}
 */
export default {
  sexMale: 1,
  sexFemale: 2,
  sexNone: 3,
  components: {
    InputInn,
    FormIdentification,
    InputSnils,
    FormCreatePatient,
    FormPatientContacts,
  },
  computed: {
    hasSavePatient() {
      return this.actions.includes('create-edit-patient');
    },
    snilsInputError: resolveInputErrors('documents.snils'),
    innInputErrors: resolveInputErrors('identification.inn'),
    /**
     * @description Собрать нарушения ограничений для конкретного визита.
     * @returns {any}
     */
    visitViolationCauses() {
      const index = this.visitList.findIndex((visit) => visit === this.visit);
      const visitViolation = {};
      Object.keys(this.violationCauses)
        .filter((causes) => causes.startsWith(`visits.${index}`))
        .forEach((causes) => {
          const keys = causes.split('.');
          visitViolation[keys.pop()] = this.violationCauses[causes];
        });
      return visitViolation;
    },
    /**
     * @description Фамилия и Имя заполнены.
     * @returns {boolean}
     */
    isNameFilled() {
      return this.patient.firstName?.length > 0
        && this.patient.lastName?.length > 0;
    },
    /**
     * @description Маска ввода серии.
     * @returns {string[]|function}
     */
    serialMask(self) {
      if (self.identification.type === 'passport') {
        return '## ##';
      }
      if (self.identification.type === 'birth_certificate') return 'XXXXXXX';
      return [''];
    },
    hasViewContact() {
      return this.actions.includes('view-patient-contacts');
    },
  },
  async created() {
    this.fetchPatientCreations();
    await this.fetchDefaultMedcardNumber();
  },
  data() {
    let contacts = new PatientContacts();
    if (this.initialPhoneNumber) {
      contacts = contacts.setPhoneList([this.initialPhoneNumber]);
    }
    return {
      /**
       * @description Допустимые действия.
       * @type {string[]}
       */
      actions: [],
      /**
       * @description Контактные данные пациента.
       * @type {PatientContacts}
       */
      contacts,
      /**
       * @description Документы.
       * @type {Documents}
       */
      documents: new Documents(),
      /**
       * @description Основные данные пациента.
       * @type {any}
       */
      patient: {
        /**
         * @description Дата рождения пациента.
         * @type {string}
         */
        birthDate: '',
        /**
         * @description Имя пациента.
         * @type {string}
         */
        firstName: '',
        /**
         * @description Фамилия пациента.
         * @type {string}
         */
        lastName: '',
        /**
         * @description Номер мед карты.
         * @type {string}
         */
        medicalCardNumber: '',
        /**
         * @description Примечание.
         * @type {string}
         */
        note: '',
        /**
         * @description Отчество пациента.
         * @type {string}
         */
        patronymic: '',
      },
      /**
         * @description Идентификатор пола.
         * @type {number|string}
         */
      sexId: '',
      /**
       * @description Удостоверение личности.
       * @type {Identification}
       */
      identification: new Identification(),
      /**
       * @description Нарушение ограничений.
       * @type {any}
       */
      violationCauses: {},
      /**
       * @description Получен ли номер медкарты пациента по умолчанию.
       * @type {bool}
       */
      isDefaultMedcardNumberFetched: false,
      /**
       * @description Проверенные телефоны.
       * @type {Array}
       */
      validPhones: [],
      /**
       * @description Найденные пациенты.
       * @type {Array}
       */
      matchedPatients: [],
    };
  },
  methods: {
    /**
     * @description Обновление удостоверения личности
     * @param newIdentification {Identification}
     */
    onUpdateIdentification(newIdentification) {
      this.identification = newIdentification;
    },
    /**
     * @description Получить данные для создания пациента.
     * @returns {void}
     */
    fetchPatientCreations() {
      const onSuccess = ({ actions }) => { this.actions = actions; };
      this.$suggestionGateway.getPatientCreation().then((result) => result.forEach(onSuccess));
    },
    /**
     * @description Обработать поиск совпадающих пациентов.
     * @returns {void}
     */
    async onMatchPatients() {
      const request = new GetMatchedPatientsRequest(
        this.patient.firstName,
        this.patient.patronymic,
        this.validPhones,
        this.patient.lastName,
      );
      const either = await this.$patientGateway.getMatchedPatients(request);
      return either.fold(
        (error) => {
          this.notifyFail(error.title);
          return [];
        },
        (result) => {
          this.matchedPatients = result;
        },
      );
    },
    /**
     * @description Ввод основных данных пациента. (проверка при потере фокуса полем)
     * @returns {void}
     */
    onCheckPatientInfo() {
      if (this.isNameFilled) {
        this.onMatchPatients();
      }
    },
    /**
     * @description Ввода данных контактов пациента.
     * @param {PatientContacts} contacts Контакты пациента.
     * @returns {void}
     */
    onInputContacts(contacts) {
      if (contacts.phoneList.length === 0) {
        return;
      }
      this.validPhones = [];
      contacts.phoneList.forEach((item) => {
        if (/^\+7[3-9][0-9]{9}$/.test(item.value)) {
          this.validPhones.push(item.value);
        }
      });
      if (this.validPhones.length > 0) {
        this.onMatchPatients();
      }
    },
    /**
     * @description Открыть страницу пациента.
     * @param {number} patientId Идентификатор пациента.
     * @returns {void}
     */
    async onSelectMatchedPatient(patientId) {
      const routeData = this.$router.resolve({ name: 'ClinicPanelPatientBasic', params: { id: patientId } });
      window.open(routeData.href, '_blank');
    },
    /**
     * @description Получить номер медкарты пациента по умолчанию.
     * @returns {void}
     */
    async fetchDefaultMedcardNumber() {
      const either = await this.$patientGateway.getDefaultPatientMedcardNumber();
      either.forEach((defaultMedcardNumber) => {
        this.patient.medicalCardNumber = defaultMedcardNumber;
        this.isDefaultMedcardNumberFetched = true;
      });
      either.forEachFail((response) => {
        this.notifyFail(response.title);
        this.isDefaultMedcardNumberFetched = true;
      });
    },
    /**
     * @description Обработать создание пациента.
     * @returns {void}
     */
    onSubmitCreatePatient() {
      const request = {
        birth_date: this.formatDateToISO(this.patient.birthDate),
        email: this.contacts.email,
        first_name: this.patient.firstName,
        is_allowed_sms: this.contacts.isAllowedSms,
        last_name: this.patient.lastName,
        medical_card_number: this.patient.medicalCardNumber,
        no_call: this.contacts.isNoCalls,
        note: this.patient.note,
        patronymic: this.patient.patronymic,
        phone_list: this.contacts.phoneList,
        sex_id: this.sexId,
        documents: {
          snils: this.documents.snils,
        },
        identification: {
          address: this.identification.address,
          living_address: this.identification.livingAddress,
          inn: this.identification.inn,
          number: this.identification.number,
          serial: this.identification.serial,
          type: this.identification.type,
          when_issued: this.formatDateToISO(this.identification.whenIssued),
          who_issued: this.identification.whoIssued,
        },
      };
      const onFail = (error) => {
        this.violationCauses = error.causes;
        if (error.title) {
          this.notifyFail(error.title);
        }
        this.$nextTick(() => {
          const firstInvalidField = document.querySelector('[aria-invalid="true"]');
          if (firstInvalidField) {
            firstInvalidField.scrollIntoView({ block: 'center', behavior: 'smooth' });
            firstInvalidField.focus({ preventScroll: true });
          }
        });
      };
      const onSuccess = ({ id, message }) => {
        this.violationCauses = {};
        this.notifySuccess(message);
        const params = {};
        if (this.initialPhoneNumber) {
          params.initialSelectedPatientId = id;
        }
        this.$router.push({
          name: 'ClinicPanelPatientList',
          params,
        });
      };
      this.$patientGateway
        .sendCreatePatient(request)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Печать страницы.
     * @returns {void}
     */
    printPage() {
      window.print();
    },
  },
  mixins: [dateMixin, focusMixin, notifierMixin, mobileMixin],
  name: 'ClinicPanelFormPatient',
  props: {
    initialPhoneNumber: { default: () => '', type: String },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.v-snack__content {
  white-space: pre-line;
}
.patient-add {
  width: 100%;
  overflow-y: scroll;
  &__validate-error {
    color: red;
    font-size: 0.8rem;
  }
  &__body {
    width: 100%;
    max-width: 1187px;
    padding: 38px 20px 48px 23px;
  }
  &__header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    margin: 0 0 40px 0;
  }
  &__title {
    color: #000000;
    margin: 0;
    &-print {
      display: none;
      font-weight: 600;
      font-size: 24px;
      line-height: 29px;
      color: #192a3e;
      margin: 0;
    }
  }
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
    color: #192a3e;
    margin: 0;
  }
  &__buttons {
    display: grid;
    grid-template-columns: 174px;
    grid-gap: 13px;
  }
  &__button {
    &-save {
      @include buttons.btn-blue(100%, 48px, 16px);
      max-width: 174px;
      padding: 0;
      margin: 0;
    }
    &-visit {
      @include buttons.btn-transparent(180px, 40px, 16px);
      @include buttons.btn-transparent-add(16px);
    }
  }
  &__search {
    position: relative;
    width: 720px;
    select {
      border-radius: 4px;
      appearance: none;
      background: #ffffff;
      border: 1px solid #c2cfe0 !important;
      color: #192a3e;
      font-size: 16px;
      font-weight: 400;
      height: 40px;
      width: 100%;
      outline: none;
      padding: 0 42px;
      &:hover {
        border: 2px solid #0489e0 !important;
        padding: 0 41px;
      }
      &:focus {
        color: #192a3e;
        background-color: #fff;
        border: 2px solid #0489e0 !important;
        outline: 0;
      }
    }
    &__icon {
      &-search {
        position: absolute;
        left: 12px;
        top: 10px;
        background-repeat: no-repeat;
        background-image: url("@/assets/img/search-black.svg");
        background-size: contain;
        width: 20px;
        height: 18px;
      }
      &-file {
        position: absolute;
        right: 1px;
        top: 1px;
        background-repeat: no-repeat;
        background-image: url("@/assets/img/file.svg");
        background-position: center;
        background-color: #eaeff3;
        border-radius: 0 3px 3px 0;
        width: 36px;
        height: 38px;
      }
    }
  }
  &__sex {
    display: flex;
    flex-flow: row wrap;
    padding: 0 0 20px 0;
    border: 0;
    gap: 10px;
    legend {
      display: contents;
    }
    &__radio > input {
      position: absolute;
      z-index: -1;
      opacity: 0;
    }
    &__radio > span {
      display: inline-flex;
      align-items: center;
      user-select: none;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
      cursor: pointer;
    }
    &__radio > span::before {
      content: "";
      display: inline-block;
      width: 24px;
      height: 24px;
      flex-shrink: 0;
      flex-grow: 0;
      border: 1px solid #90a0b7;
      border-radius: 50%;
      margin-right: 10px;
      background-repeat: no-repeat;
      background-position: center center;
      background-size: 50% 50%;
    }
    &__radio > input:not(:disabled):not(:checked) + span:hover::before {
      border: 1px solid #0489e0;
    }
    &__radio > input:checked + span::before {
      border: 1px solid #0489e0;
      background-color: #0489e0;
      box-shadow: inset 0 0 0 6px #fff;
    }
  }
  &__contact {
    background: #f2f5f8;
    padding: 35px 25px 38px 25px;
    display: flex;
    flex-direction: column;
    gap: 26px;
  }
  &__double {
    display: flex;
    flex-direction: column;
    div {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 5px;
      margin: 5px 0 0 0;
      font-weight: 400;
      font-size: 16px;
      line-height: 19px;
    }
    &__title {
      color: red;
    }
    &__error:hover {
      color: #0389e0;
    }
  }
  &__documents {
    background: #f2f5f8;
    border-bottom: 1px solid #c2cfe0;
    padding: 35px 25px 48px 25px;
    margin: 26px 0 0 0;
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 27px;
    &-snils {
      width: 333px;
    }
    &-belay {
      width: 720px;
    }
    &-date {
      width: 266px;
    }
  }
  &__ticket {
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 27px;
    background: #f2f5f8;
    padding: 35px 25px 48px 25px;
    margin: 0 0 22px 0;
    &__row {
      display: grid;
      grid-template-columns: 95px 161px;
      grid-gap: 12px;
    }
  }
  &__identification {
    width: min(100%, 730px);
    .form-identification {
      h3 {
        font-size: 24px;
      }
    }
  }
  &__visit {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin: 64px 0 18px 0;
  }
}
@media print {
  .clinic-panel {
    height: 100%;
  }
  .patient-add {
    height: 100%;
    margin: 0 1cm;
    &__header {
      margin: 0 0 20px 0;
    }
    &__body {
      padding: 10px 0;
      margin: 0 auto;
    }
    &__buttons {
      display: none;
    }
    &__button {
      &-save {
        display: none;
      }
    }
    &__title {
      &-print {
        display: flex;
      }
    }
    &__grid {
      grid-template-columns: 1fr;
      grid-gap: 0;
      &__header {
        grid-column-start: 1;
        grid-column-end: 2;
      }
      &__body {
        grid-column-start: 1;
        grid-column-end: 2;
        grid-template-columns: 1fr;
      }
      &__footer {
        grid-column-start: 1;
        grid-column-end: 2;
        width: 90%;
      }
    }
    &__contact {
      margin: 10px 0 0 0;
      padding: 0;
      background-color: transparent;
    }
    &__double {
      display: flex;
      flex-direction: column;
      div {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 5px;
        margin: 5px 0 0 0;
        font-weight: 400;
        font-size: 16px;
        line-height: 19px;
      }
      &__title {
        color: red;
      }
      &__error:hover {
        color: #0389e0;
      }
    }
    &__documents {
      margin: 10px 0 0 0;
      padding: 0;
      background-color: transparent;
      grid-gap: 0;
      border-bottom: none;
    }
    &__ticket {
      margin: 10px 0 0 0;
      padding: 0;
      background-color: transparent;
      grid-gap: 0;
      &__row {
        grid-gap: 0;
        grid-template-columns: 1fr;
      }
    }
    &__search {
      width: 100%;
      &__icon-search,
      &__icon-file {
        display: none;
      }
    }
    &__caption {
      margin: 0 0 10px 0;
    }
  }
  .head-panel {
    display: none;
  }
  input {
    padding: 0 !important;
    margin: 0 !important;
    border: none !important;
  }
  .page {
    display: block;
  }
  .noprint,
  .counter,
  .datetime-reset-button {
    display: none !important;
  }
}
@media screen and (max-width: 1240px) {
  .patient-add {
    &__body {
      padding: 30px 20px 115px 20px;
    }
  }
}
@media screen and (max-width: 960px) {
  .patient-add {
    &__header {
      flex-direction: column;
      align-items: flex-start;
    }
    &__buttons {
      margin: 30px 0 0 0;
    }
    &__documents {
      &-belay,
      &-date,
      &-snils {
        width: 100%;
      }
    }
    &__identification {
      width: 100%;
    }
    &__search {
      width: 100%;
    }
  }
}
@media screen and (max-width: 680px) {
  .patient-add {
    &__ticket {
      &__row {
        grid-template-columns: 1fr;
        grid-gap: 27px;
      }
    }
  }
}
@media screen and (max-width: 480px) {
  .patient-add {
    &__buttons {
      grid-template-columns: 1fr;
      width: 100%;
    }
    &__button {
      &-print {
        max-width: 100%;
      }
      &-save {
        max-width: 100%;
      }
    }
    &__documents {
      margin: 26px -20px 0 -20px;
    }
    &__ticket {
      margin: 0 -20px 22px -20px;
    }
  }
}
</style>
